// @flow

import React from "react";
import { format } from "date-fns";
import classNames from "classnames";

import optionsResolver from "../utils/options";
import { DatePicker } from "@components";
import type { SchemaTableWidgetProps } from "../types";

export type DatePickerWidgetState = $Exact<{
  touched: boolean,
}>;

export class SchemaTableDatePicker extends React.Component<
  SchemaTableWidgetProps<string>,
  DatePickerWidgetState,
> {
  state = {
    touched: false,
  };

  componentDidUpdate(prevProps: SchemaTableWidgetProps<string>) {
    // this will only happen as part of a request to hide errors
    if (!this.props.touched && prevProps.touched) {
      this.setState({ touched: false });
    }
  }

  handleChange = (output: *) => {
    this.setState({ touched: true });

    if (!output || output === "Invalid Date") {
      this.props.onChange(null);
      return;
    }

    if (this.props.format === "date") {
      this.props.onChange(format(output, "YYYY-MM-DD"));
      return;
    }

    if (this.props.format === "time") {
      this.props.onChange(format(output, "hh:mm:ss"));
      return;
    }

    this.props.onChange(format(output, "YYYY-MM-DDThh:mm:ssZ"));
  };

  hasUiSchemaError = (value: string): boolean => {
    if (!this.state.touched) {
      return false;
    }

    if (this.props.required && !value) {
      return true;
    }

    const options = this.props.options || {};

    const minDate = optionsResolver.resolveDate(options.minDate, this.props.row);
    if (minDate && new Date(value) < minDate) {
      return true;
    }

    const maxDate = optionsResolver.resolveDate(options.maxDate, this.props.row);
    if (maxDate && new Date(value) > maxDate) {
      return true;
    }

    return false;
  };

  hasError = (value: string): boolean => {
    return !!this.props.error || this.hasUiSchemaError(value);
  };

  formatDate = (value: string) =>
    (parsed => (!value || parsed === "Invalid Date" ? value : parsed))(format(value, "DD/MM/YYYY"));

  render() {
    const disabled = optionsResolver.resolveBoolean(
      (this.props.options || {}).disabled,
      this.props.row,
    );

    if (!this.props.active) {
      return (
        <div
          className={classNames("date-picker__readonly text-color-black align-self-center", {
            text__disabled: disabled,
          })}
        >
          {this.formatDate(this.props.data)}
        </div>
      );
    }

    const { options } = this.props;
    let optionalProps: Object = {};
    if (options && (options.minDate || options.maxDate)) {
      if (options.minDate) {
        optionalProps.minDate = optionsResolver.resolveDate(options.minDate, this.props.row);
      }

      if (options.maxDate) {
        optionalProps.maxDate = optionsResolver.resolveDate(options.maxDate, this.props.row);
      }
    }

    return (
      <div
        className={classNames("date-picker__container", {
          "date-picker__container--disabled": disabled,
          "date-picker__container--error": this.hasError(this.props.data),
        })}
      >
        <DatePicker
          className="DatePicker"
          value={this.props.data}
          onChange={this.handleChange}
          disabled={disabled}
          {...optionalProps}
        />
      </div>
    );
  }
}

export default SchemaTableDatePicker;
